import { z } from "zod";
import { parseStoryPublishTag } from "./channel-catalog";

/**
 * Authoring-layer schemas for the Video Project pipeline
 * (story / documentary / news / advertisement).
 * Separate from VideoShorts. Persisted on VideoProject.document / inputBrief / validation.
 */

/** LLMs often emit `null` for omitted optionals; treat as undefined. */
function nullishToOptional<T extends z.ZodTypeAny>(schema: T) {
	return z.preprocess((value) => (value === null ? undefined : value), schema);
}

export const VideoProjectFormatSchema = z.enum([
	"story",
	"documentary",
	"news",
	"advertisement",
]);
export type VideoProjectFormat = z.infer<typeof VideoProjectFormatSchema>;

export const VideoAspectSchema = z.enum(["portrait", "landscape", "square"]);
export type VideoAspect = z.infer<typeof VideoAspectSchema>;

export const TtsProviderSchema = z.enum(["gemini", "openai"]);
export type TtsProvider = z.infer<typeof TtsProviderSchema>;

export const VoiceGenderSchema = z.enum(["male", "female"]);
export type VoiceGender = z.infer<typeof VoiceGenderSchema>;

export const VisualModalitySchema = z.enum(["still", "ai_video", "none"]);
export type VisualModality = z.infer<typeof VisualModalitySchema>;

/**
 * Advertisement creative style (chosen on create form).
 * - product_showcase: product in changing places, smooth slow motion
 * - magical_energetic: place jumps + effects + short fantasy/story beats
 */
export const AdvertisementStyleSchema = z.enum([
	"product_showcase",
	"magical_energetic",
]);
export type AdvertisementStyle = z.infer<typeof AdvertisementStyleSchema>;
export const ADVERTISEMENT_STYLE_DEFAULT: AdvertisementStyle =
	"product_showcase";

/**
 * How this plate chains frames from the previous plate (Wan source / transition).
 * - fresh: this plate’s still as source; no transition_image
 * - morph_from_prev: prev lastFrame → morph → this still (generative)
 * - continue_from_prev: prev lastFrame is the source (continuation)
 */
export const PlateFrameContinuitySchema = z.enum([
	"fresh",
	"morph_from_prev",
	"continue_from_prev",
]);
export type PlateFrameContinuity = z.infer<typeof PlateFrameContinuitySchema>;

/** Where a plate's media came from after generate. */
export const PlateMediaSourceSchema = z.enum(["stock", "ai"]);
export type PlateMediaSource = z.infer<typeof PlateMediaSourceSchema>;

/**
 * How this plate enters from the previous plate (junction into this plate).
 * - none: hard cut (no compile transition, no Wan morph)
 * - compile: editor transition at compile time (fade/wipe/…)
 * - generative: Wan 2.2 transition_image morph (baked into the clip; no compile transition)
 */
export const PlateEnterTransitionSchema = z.enum([
	"none",
	"compile",
	"generative",
]);
export type PlateEnterTransition = z.infer<typeof PlateEnterTransitionSchema>;

export const PLATE_ENTER_TRANSITION_DEFAULT: PlateEnterTransition = "compile";

/**
 * Story + advertisement use startFrame / endFrame / videoPrompt continuity.
 * News + documentary keep the legacy single-prompt + imageUrl path.
 */
export function usesCinematicPlateFrames(
	format: string | null | undefined,
): boolean {
	return format === "story" || format === "advertisement";
}

/**
 * How a plate frame image is obtained.
 * - generate: run still gen from prompt (+ optional frame refs)
 * - alias_previous_end: reuse previous plate’s endFrame URL (no gen)
 */
export const PlateFrameSourceSchema = z.enum([
	"generate",
	"alias_previous_end",
]);
export type PlateFrameSource = z.infer<typeof PlateFrameSourceSchema>;

/** Sibling / previous frames that may be attached as image refs when generating. */
export const PlateFrameReferenceRoleSchema = z.enum([
	"start",
	"end",
	"previous_start",
	"previous_end",
]);
export type PlateFrameReferenceRole = z.infer<
	typeof PlateFrameReferenceRoleSchema
>;

/** Async AI clip job (Modal Wan 2.2). Veo writes `ready` immediately. */
export const PlateVideoStatusSchema = z.enum(["pending", "ready", "failed"]);
export type PlateVideoStatus = z.infer<typeof PlateVideoStatusSchema>;

export const StockProviderSchema = z.enum([
	"pexels",
	"wikimedia",
	"tavily",
	"google",
]);
export type StockProvider = z.infer<typeof StockProviderSchema>;

/**
 * Beat structural roles. Story uses dramatic arc kinds; documentary / news use
 * their own vocabularies (legacy story kinds still accepted for older docs).
 */
export const VideoBeatKindSchema = z.enum([
	// Story arc
	"hook",
	"setup",
	"scene",
	"proof",
	"twist",
	"cta",
	"payoff",
	"logo",
	// Documentary
	"context",
	"evidence",
	"human",
	"closing",
	// News
	"lead",
	"facts",
	"update",
	"close",
	// Advertisement / commercial
	"problem",
	"demo",
	"benefit",
	"offer",
	"disclaimer",
]);
export type VideoBeatKind = z.infer<typeof VideoBeatKindSchema>;

/** Preferred beat kinds for authoring / UI per format. */
export const VIDEO_PROJECT_BEAT_KINDS_BY_FORMAT: Record<
	VideoProjectFormat,
	readonly VideoBeatKind[]
> = {
	story: ["hook", "setup", "scene", "proof", "twist", "cta", "payoff", "logo"],
	documentary: ["hook", "context", "evidence", "human", "closing", "logo"],
	news: ["lead", "facts", "context", "update", "close", "logo"],
	advertisement: [
		"problem",
		"demo",
		"benefit",
		"offer",
		"cta",
		"disclaimer",
		"logo",
	],
};

/** Brand end-card (`kind: "logo"`) must hold long enough for the corner→center move. */
export const MIN_LOGO_BEAT_MS = 2500;

export function beatKindsForFormat(
	format: VideoProjectFormat | string | undefined,
): readonly VideoBeatKind[] {
	if (format === "news") return VIDEO_PROJECT_BEAT_KINDS_BY_FORMAT.news;
	if (format === "documentary")
		return VIDEO_PROJECT_BEAT_KINDS_BY_FORMAT.documentary;
	if (format === "advertisement")
		return VIDEO_PROJECT_BEAT_KINDS_BY_FORMAT.advertisement;
	return VIDEO_PROJECT_BEAT_KINDS_BY_FORMAT.story;
}

export function formatAllowsCharactersByDefault(
	format: VideoProjectFormat | string | undefined,
): boolean {
	return format === "story";
}

export function formatPrefersRealFootage(
	format: VideoProjectFormat | string | undefined,
): boolean {
	return format === "documentary" || format === "news";
}

/**
 * Whether authoring should run a grounded research pass before bible/script.
 * News/documentary always; story when preferRealFootage is explicitly true.
 * Advertisement never requires fact research (optional inspiration only, not here).
 */
export function videoProjectNeedsResearch(input: {
	format?: VideoProjectFormat | string | null;
	preferRealFootage?: boolean | null;
}): boolean {
	if (input.format === "advertisement") return false;
	if (input.preferRealFootage === true) return true;
	return formatPrefersRealFootage(input.format ?? undefined);
}

/** Prop continuity role — product photos are locked identity refs for ads. */
export const VideoPropRoleSchema = z.enum(["prop", "product"]);
export type VideoPropRole = z.infer<typeof VideoPropRoleSchema>;

const CTA_URL_BLOCKED_HOSTS = new Set([
	"example.com",
	"www.example.com",
	"example.org",
	"example.net",
	"localhost",
	"bit.ly",
	"t.co",
	"tinyurl.com",
	"goo.gl",
	"ow.ly",
	"buff.ly",
	"rebrand.ly",
	"cutt.ly",
	"is.gd",
	"tiny.cc",
]);

const CTA_URL_BLOCKED_HOST_SUFFIXES = [
	".wikipedia.org",
	".wikimedia.org",
	".example.com",
];

/**
 * Drop LLM-invented CTA links. A URL is kept only when it is https,
 * not a blocked junk/shortener/encyclopedia host, and the host matches
 * `allowHosts` (org/brand domains). With no allowlist, the URL is omitted.
 */
export function sanitizeVideoProjectCtaUrl(
	value: unknown,
	options?: { allowHosts?: string[] },
): string | undefined {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	if (!trimmed) return undefined;

	let parsed: URL;
	try {
		parsed = new URL(trimmed);
	} catch {
		return undefined;
	}
	if (parsed.protocol !== "https:") return undefined;

	const host = parsed.hostname.toLowerCase();
	if (!host || host === "localhost" || host.endsWith(".localhost"))
		return undefined;
	if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return undefined;
	if (CTA_URL_BLOCKED_HOSTS.has(host)) return undefined;
	if (
		CTA_URL_BLOCKED_HOST_SUFFIXES.some(
			(suffix) => host === suffix.slice(1) || host.endsWith(suffix),
		)
	)
		return undefined;

	const allowHosts = (options?.allowHosts ?? [])
		.map((item) => item.trim().toLowerCase())
		.filter(Boolean);
	if (allowHosts.length === 0) return undefined;
	const isAllowed = allowHosts.some(
		(allowed) => host === allowed || host.endsWith(`.${allowed}`),
	);
	if (!isAllowed) return undefined;
	return parsed.href;
}

const GENERIC_SOCIAL_FIRST_COMMENT = [
	/drop a comment/i,
	/schreib'?s in die kommentare/i,
	/dites-le en commentaire/i,
	/اكتب في التعليقات/,
	/^(what do you think|thoughts|vous en pensez quoi|was denkst du|ما رأيك)\??$/i,
];

/** Pinned/first social comment written for this video. Strips URLs and generic bait. */
export function sanitizeSocialMediaFirstComment(
	value: unknown,
): string | undefined {
	if (typeof value !== "string") return undefined;
	let text = value.replace(/https?:\/\/\S+/gi, " ").replace(/\s+/g, " ").trim();
	if (!text) return undefined;
	if (GENERIC_SOCIAL_FIRST_COMMENT.some((pattern) => pattern.test(text)))
		return undefined;
	if (text.length > 400) text = text.slice(0, 400).trimEnd();
	return text || undefined;
}

/** Cited web source from the grounded research step (news/documentary). */
export const VideoProjectSourceSchema = z.object({
	id: z.string().min(1),
	title: z.string().min(1),
	url: z.string().url(),
	publisher: z.string().optional(),
	retrievedAt: z.string().optional(),
	snippet: z.string().optional(),
});
export type VideoProjectSource = z.infer<typeof VideoProjectSourceSchema>;

/** Where a harvestable still URL came from (user paste, research, or trending). */
export const VideoProjectVisualRefOriginSchema = z.enum([
	"user",
	"research",
	"trending",
]);
export type VideoProjectVisualRefOrigin = z.infer<
	typeof VideoProjectVisualRefOriginSchema
>;

/**
 * A real-world image (or source page) to try before stock search / AI stills.
 * `url` is the image file when possible; `sourcePageUrl` is the page it came from.
 */
export const VideoProjectVisualRefSchema = z.object({
	id: z.string().min(1),
	url: z.string().url(),
	context: z.string().max(400).optional(),
	sourcePageUrl: z.string().url().optional().or(z.literal("")),
	provider: StockProviderSchema.optional(),
	origin: VideoProjectVisualRefOriginSchema.optional(),
});
export type VideoProjectVisualRef = z.infer<typeof VideoProjectVisualRefSchema>;

export const PlateMediaProvenanceSchema = z.object({
	mediaSource: PlateMediaSourceSchema,
	stockProvider: StockProviderSchema.optional(),
	stockId: z.string().optional(),
	attribution: z.string().optional(),
	sourcePageUrl: z.string().url().optional().or(z.literal("")),
	/** Original remote file URL (before S3 rehost) — used to avoid reusing the same photo. */
	stockFileUrl: z.string().url().optional().or(z.literal("")),
});
export type PlateMediaProvenance = z.infer<typeof PlateMediaProvenanceSchema>;

export const DialogueSpeakerSchema = z.enum(["narrator", "character"]);
export type DialogueSpeaker = z.infer<typeof DialogueSpeakerSchema>;

/**
 * Plyven-style structured image prompt details (stored on VisualPrompt.json).
 * Used when composing the final Gemini prompt and resolving continuity refs.
 */
export const VisualPromptDetailsSchema = z
	.object({
		style: z.string().optional(),
		lighting: z.string().optional(),
		mood: z.string().optional(),
		camera_angle: z.string().optional(),
		time_of_day: z.string().optional(),
		weather: z.string().optional(),
		season: z.string().optional(),
		color_palette: z.string().optional(),
		setting_type: z.string().optional(),
		era_period: z.string().optional(),
		visible_objects: z.string().optional(),
		visible_persons: z.string().optional(),
		visible_persons_count: z.union([z.number(), z.string()]).optional(),
		foreground_elements: z.string().optional(),
		background_elements: z.string().optional(),
		composition_focus: z.string().optional(),
		/** Character ids whose reference images must be attached */
		reference_npcs: z.array(z.string()).optional(),
		/** Prop/item ids whose reference images must be attached */
		reference_items: z.array(z.string()).optional(),
		/** Setting/place ids (video-project extension; used on beats/poster) */
		reference_settings: z.array(z.string()).optional(),
		additional_params: z.string().optional(),
	})
	.passthrough();
export type VisualPromptDetails = z.infer<typeof VisualPromptDetailsSchema>;

/** Narrator / project global look — style only (mood, lighting, camera stay per-plate). */
export const NarratorVisualDefaultsSchema = z.object({
	style: z.string().min(1),
});
export type NarratorVisualDefaults = z.infer<typeof NarratorVisualDefaultsSchema>;

/** Parse stored narrator / project visual defaults from JSON. */
export function parseVisualDefaults(
	value: unknown,
): VisualPromptDetails | undefined {
	if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
	const parsed = VisualPromptDetailsSchema.safeParse(value);
	if (!parsed.success) return undefined;
	const hasValue = Object.values(parsed.data).some((entry) => {
		if (entry == null || entry === "") return false;
		if (Array.isArray(entry)) return entry.length > 0;
		return true;
	});
	return hasValue ? parsed.data : undefined;
}

/** Parse narrator/project visual defaults — only the global `style` field. */
export function parseNarratorVisualDefaults(
	value: unknown,
): NarratorVisualDefaults | undefined {
	const parsed = parseVisualDefaults(value);
	const style = parsed?.style?.trim();
	return style ? { style } : undefined;
}

/** Fill empty plate prompt.json `style` from narrator/project defaults. */
export function mergeVisualPromptDetails(
	plate: VisualPromptDetails | undefined | null,
	defaults: VisualPromptDetails | undefined | null,
): VisualPromptDetails | undefined {
	const styleDefault =
		defaults && typeof defaults.style === "string" && defaults.style.trim()
			? defaults.style.trim()
			: undefined;
	const overlay = plate && typeof plate === "object" ? plate : {};
	const merged: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(overlay)) {
		if (value == null || value === "") continue;
		if (Array.isArray(value) && value.length === 0) continue;
		merged[key] = value;
	}
	if (styleDefault) {
		merged.style = styleDefault;
	}
	if (Object.keys(merged).length === 0) return undefined;
	return merged as VisualPromptDetails;
}

export const VisualPromptSchema = z.object({
	/** Natural-language image prompt (Plyven "Image prompt") */
	prompt: z.string().min(1),
	/** Structured prompt details (Plyven "Prompt details") */
	json: VisualPromptDetailsSchema.optional(),
	negative: z.string().optional(),
	/** Legacy / generic asset ids after upload */
	referenceAssetIds: z.array(z.string()).optional(),
});
export type VisualPrompt = z.infer<typeof VisualPromptSchema>;

export const NarratorConfigSchema = z.object({
	name: z.string().min(1),
	gender: VoiceGenderSchema,
	provider: TtsProviderSchema,
	voiceId: z.string().min(1),
	/**
	 * Narration persona (e.g. sarcastic, poetic) — script generation only.
	 * TTS delivery uses ttsStyle / ttsPace / ttsAccent instead.
	 */
	personality: z.string().min(1).optional(),
	/** Authoring notes for LLM script writing — not sent to TTS. */
	styleNotes: z.string().optional(),
	/** Gemini Director's Notes — Style (snapshotted from org profile). */
	ttsStyle: z.string().min(1).optional(),
	/** Gemini Director's Notes — Pace. */
	ttsPace: z.string().min(1).optional(),
	/** Gemini Director's Notes — Accent. */
	ttsAccent: z.string().min(1).optional(),
	/** Optional extra TTS director notes. */
	ttsNotes: z.string().optional(),
	/**
	 * Public channel / show name (e.g. "Midnight Cases"). Landscape story host mode.
	 * Snapshotted from org narrator profile on project create.
	 */
	channelName: z.string().min(1).optional(),
	/** Optional sign-off phrase for channel outros. Script generation only. */
	channelSignOff: z.string().min(1).optional(),
	/**
	 * Optional on-screen text overlay (e.g. channel tagline). Empty = skipped at compile.
	 * Snapshotted from org narrator profile; editable per project.
	 */
	overlayText: z.string().min(1).optional(),
	/** Delay before overlay text appears (ms from timeline start). */
	overlayTextDelayMs: z.number().int().nonnegative().optional(),
	/**
	 * How long overlay text stays visible (ms).
	 * `null` = remain until the end of the video.
	 */
	overlayTextDurationMs: z.number().int().positive().nullable().optional(),
	/**
	 * Playback multiplier for narrator TTS (0.75 slow → 1.35 fast).
	 * Snapshotted from org narrator profile on project create.
	 */
	speechSpeed: nullishToOptional(
		z.number().min(0.75).max(1.35).optional(),
	),
	/** Org profile this narrator was snapshotted from (provenance). */
	profileId: z.string().min(1).optional(),
	/** Optional still-image AppFile snapshotted from the org narrator profile. */
	avatarFileAppId: z.string().min(1).optional(),
});
export type NarratorConfig = z.infer<typeof NarratorConfigSchema>;

/** Default delay before narrator overlay text appears. */
export const NARRATOR_OVERLAY_TEXT_DELAY_MS = 2_000;
export const NARRATOR_OVERLAY_TEXT_DELAY_MAX_MS = 120_000;
export const NARRATOR_OVERLAY_TEXT_DURATION_MAX_MS = 600_000;

/** Default PIP clip length and cadence for narrator avatars. */
export const NARRATOR_AVATAR_MIN_DURATION_MS = 6_000;
export const NARRATOR_AVATAR_MAX_DURATION_MS = 10_000;
export const NARRATOR_AVATAR_APPEAR_EVERY_MS = 60_000;
/** First appearance stays full-frame this long, then shrinks into the PIP. */
export const NARRATOR_AVATAR_INTRO_HOLD_MS = 2_000;
export const NARRATOR_AVATAR_INTRO_HOLD_MAX_MS = 15_000;
/** Extra wait after the beat visual starts before the first avatar intro. */
export const NARRATOR_AVATAR_AFTER_CUT_MS = 800;
export const NARRATOR_AVATAR_AFTER_CUT_MAX_MS = 5_000;

export const NarratorAvatarTalkStatusSchema = z.union([
	z.literal("pending"),
	z.literal("ready"),
	z.literal("failed"),
]);
export type NarratorAvatarTalkStatus = z.infer<
	typeof NarratorAvatarTalkStatusSchema
>;

export const NarratorAvatarAppearanceSchema = z.object({
	fromMs: z.number().nonnegative(),
	toMs: z.number().positive(),
	/** Beat this window is fully contained in (never spans beats). */
	beatId: z.string().min(1).optional(),
	reason: z.string().optional(),
	talkStatus: NarratorAvatarTalkStatusSchema.optional(),
	talkUrl: z.string().url().optional().or(z.literal("")),
	talkJobId: z.string().min(1).optional(),
	talkError: z.string().optional(),
	/** Timeline start of the talking clip (may be shorter than the PIP window). */
	talkFromMs: z.number().nonnegative().optional(),
	talkDurationMs: z.number().int().positive().optional(),
	/** 1× InfiniteTalk / driving-audio window length before speechSpeed playbackRate. */
	talkSourceDurationMs: z.number().int().positive().optional(),
	/** Acting and emotion prompt sent to InfiniteTalk for lip-sync & facial expression. */
	talkPrompt: z.string().optional(),
});
export type NarratorAvatarAppearance = z.infer<
	typeof NarratorAvatarAppearanceSchema
>;

export const VideoProjectNarratorAvatarSchema = z.object({
	fileAppId: z.string().min(1).optional(),
	url: z.string().url().optional().or(z.literal("")),
	minDurationMs: z
		.number()
		.int()
		.positive()
		.default(NARRATOR_AVATAR_MIN_DURATION_MS),
	maxDurationMs: z
		.number()
		.int()
		.positive()
		.default(NARRATOR_AVATAR_MAX_DURATION_MS),
	appearEveryMs: z
		.number()
		.int()
		.positive()
		.default(NARRATOR_AVATAR_APPEAR_EVERY_MS),
	introHoldMs: z
		.number()
		.int()
		.nonnegative()
		.default(NARRATOR_AVATAR_INTRO_HOLD_MS),
	afterCutMs: z
		.number()
		.int()
		.nonnegative()
		.default(NARRATOR_AVATAR_AFTER_CUT_MS),
	appearances: z.array(NarratorAvatarAppearanceSchema).default([]),
});
export type VideoProjectNarratorAvatar = z.infer<
	typeof VideoProjectNarratorAvatarSchema
>;

export function normalizeNarratorAvatarTiming(input?: {
	minDurationMs?: number | null;
	maxDurationMs?: number | null;
	appearEveryMs?: number | null;
	introHoldMs?: number | null;
	afterCutMs?: number | null;
}): {
	minDurationMs: number;
	maxDurationMs: number;
	appearEveryMs: number;
	introHoldMs: number;
	afterCutMs: number;
} {
	const minRaw =
		typeof input?.minDurationMs === "number" &&
		Number.isFinite(input.minDurationMs)
			? Math.round(input.minDurationMs)
			: NARRATOR_AVATAR_MIN_DURATION_MS;
	const maxRaw =
		typeof input?.maxDurationMs === "number" &&
		Number.isFinite(input.maxDurationMs)
			? Math.round(input.maxDurationMs)
			: NARRATOR_AVATAR_MAX_DURATION_MS;
	const everyRaw =
		typeof input?.appearEveryMs === "number" &&
		Number.isFinite(input.appearEveryMs)
			? Math.round(input.appearEveryMs)
			: NARRATOR_AVATAR_APPEAR_EVERY_MS;
	const holdRaw =
		typeof input?.introHoldMs === "number" && Number.isFinite(input.introHoldMs)
			? Math.round(input.introHoldMs)
			: NARRATOR_AVATAR_INTRO_HOLD_MS;
	const afterCutRaw =
		typeof input?.afterCutMs === "number" && Number.isFinite(input.afterCutMs)
			? Math.round(input.afterCutMs)
			: NARRATOR_AVATAR_AFTER_CUT_MS;
	const minDurationMs = Math.max(1_000, minRaw);
	const maxDurationMs = Math.max(minDurationMs, maxRaw);
	const appearEveryMs = Math.max(minDurationMs, everyRaw);
	const introHoldMs = Math.max(
		0,
		Math.min(NARRATOR_AVATAR_INTRO_HOLD_MAX_MS, holdRaw),
	);
	const afterCutMs = Math.max(
		0,
		Math.min(NARRATOR_AVATAR_AFTER_CUT_MAX_MS, afterCutRaw),
	);
	return {
		minDurationMs,
		maxDurationMs,
		appearEveryMs,
		introHoldMs,
		afterCutMs,
	};
}

export function narratorAvatarTimingFromProfile(profile?: {
	avatarMinDurationMs?: number | null;
	avatarMaxDurationMs?: number | null;
	avatarAppearEveryMs?: number | null;
	avatarIntroHoldMs?: number | null;
} | null) {
	return normalizeNarratorAvatarTiming({
		minDurationMs: profile?.avatarMinDurationMs ?? undefined,
		maxDurationMs: profile?.avatarMaxDurationMs ?? undefined,
		appearEveryMs: profile?.avatarAppearEveryMs ?? undefined,
		introHoldMs: profile?.avatarIntroHoldMs ?? undefined,
	});
}

export const NARRATOR_SPEECH_SPEED_MIN = 0.75;
export const NARRATOR_SPEECH_SPEED_MAX = 1.35;
export const NARRATOR_SPEECH_SPEED_DEFAULT = 1;

export function normalizeNarratorSpeechSpeed(value: unknown): number {
	if (typeof value !== "number" || !Number.isFinite(value))
		return NARRATOR_SPEECH_SPEED_DEFAULT;
	return Math.min(
		NARRATOR_SPEECH_SPEED_MAX,
		Math.max(NARRATOR_SPEECH_SPEED_MIN, value),
	);
}

/** Timeline duration after applying narrator speechSpeed to raw TTS length. */
export function effectiveSpeechDurationMs(
	rawMs: number,
	speechSpeed?: number,
): number {
	const speed = normalizeNarratorSpeechSpeed(speechSpeed);
	return Math.max(1, Math.round(rawMs / speed));
}

export function speechSpeedInstruction(speechSpeed?: number): string | undefined {
	const speed = normalizeNarratorSpeechSpeed(speechSpeed);
	if (speed <= 0.88)
		return "Pacing: speak noticeably slower than normal conversational speed.";
	if (speed >= 1.12)
		return "Pacing: speak briskly — faster than normal conversational speed, without rushing words.";
	return undefined;
}

/** Suggested narrator personalities for UI presets (free-form values still allowed). */
export const NARRATOR_PERSONALITY_PRESETS = [
	"neutral",
	"sarcastic",
	"poetic",
	"aggressive",
	"funny",
	"warm",
	"dramatic",
	"calm",
	"authoritative",
	"curious",
] as const;
export type NarratorPersonalityPreset =
	(typeof NARRATOR_PERSONALITY_PRESETS)[number];

/**
 * Full style briefs for preset personalities (authoring + TTS).
 * Custom personality strings are passed through unchanged.
 */
export const NARRATOR_PERSONALITY_BRIEFS: Record<
	NarratorPersonalityPreset,
	string
> = {
	neutral:
		"Clear, measured narration. Plain language, even pacing, no gimmicks. Prefer facts and concrete images over flourish or jokes.",
	sarcastic:
		"Dry irony and understatement. Point out absurdity without cartoonish insults or cruelty. Short cutting asides are welcome; never sincere cheerleading or melodrama.",
	poetic:
		"Lyrical, image-rich language with spoken-verse cadence. Prefer soft internal rhyme, occasional end-rhyme where it feels natural, alliteration, and rhythmic phrasing — like poetry read aloud, not forced song lyrics on every line. Use metaphor and sensory detail; keep the story clear and never sacrifice meaning for a rhyme.",
	aggressive:
		"Urgent, confrontational energy. Short punchy sentences, strong verbs, rhetorical pressure. Intense and direct, but avoid gratuitous gore, slurs, or empty shouting.",
	funny:
		"Witty, light observational humor with timing and contrast. Occasional punchlines and playful asides are fine. Never undermine serious facts with cruel jokes about victims or tragedy.",
	warm:
		"Empathetic, inviting tone. Gentle pacing, inclusive wording, and soft diction. Reassure without fluff or false cheer.",
	dramatic:
		"High-stakes cinematic delivery. Build tension, pause for reveals, and use emphatic contrasts. Theatrical and vivid, but not campy or overacted.",
	calm:
		"Slow, steady, soothing delivery. Minimal intensity spikes, smooth transitions, quiet authority. Let silence and simplicity carry weight.",
	authoritative:
		"Expert documentary-host voice. Precise wording, confident structure, and gravitas. Explain clearly; avoid slang, jokes, and emotional excess.",
	curious:
		"Inquisitive guide. Use questions, wonder, and “what if” framing that invites the viewer to investigate. Open, exploratory phrasing without sounding naive.",
};

/** Short UI hint under each preset. */
export const NARRATOR_PERSONALITY_HINTS: Record<
	NarratorPersonalityPreset,
	string
> = {
	neutral: "Plain, even, fact-first",
	sarcastic: "Dry irony, cutting asides",
	poetic: "Rhyme, rhythm, lyrical imagery",
	aggressive: "Urgent, punchy, confrontational",
	funny: "Witty observational humor",
	warm: "Gentle, empathetic, inviting",
	dramatic: "Cinematic tension and reveals",
	calm: "Slow, soothing, quiet authority",
	authoritative: "Expert host, precise gravitas",
	curious: "Questions, wonder, investigation",
};

export function isNarratorPersonalityPreset(
	value: string,
): value is NarratorPersonalityPreset {
	return (NARRATOR_PERSONALITY_PRESETS as readonly string[]).includes(
		value.trim().toLowerCase(),
	);
}

/**
 * Expand a preset label into a full brief; custom text is returned trimmed as-is.
 */
export function resolveNarratorPersonalityPrompt(
	personality: string | null | undefined,
): string | undefined {
	const raw = personality?.trim();
	if (!raw) return undefined;
	const key = raw.toLowerCase();
	if (isNarratorPersonalityPreset(key))
		return NARRATOR_PERSONALITY_BRIEFS[key];
	return raw;
}

export const NarratorAvatarPhotoSchema = z.object({
	fileAppId: z.string().min(1),
	aspectRatio: VideoAspectSchema,
	name: z.string().optional(),
	url: z.string().optional(),
});
export type NarratorAvatarPhoto = z.infer<typeof NarratorAvatarPhotoSchema>;

/**
 * Org-scoped reusable narrator profile (CRUD payload).
 * Snapshotted into VideoProjectInputBrief.narrator on create.
 */
export const NarratorProfileSchema = z.object({
	id: z.string().min(1).optional(),
	name: z.string().min(1),
	description: z.string().optional().nullable(),
	narratorName: z.string().min(1),
	gender: VoiceGenderSchema,
	provider: TtsProviderSchema,
	voiceId: z.string().min(1),
	personality: z.string().min(1),
	styleNotes: z.string().optional().nullable(),
	ttsStyle: z.string().min(1).optional().default("neutral"),
	ttsPace: z.string().min(1).optional().default("natural"),
	ttsAccent: z.string().min(1).optional().default("american_neutral"),
	ttsNotes: z.string().optional().nullable(),
	channelName: z.string().min(1).optional().nullable(),
	channelSignOff: z.string().min(1).optional().nullable(),
	overlayText: z.string().min(1).optional().nullable(),
	overlayTextDelayMs: z.number().int().nonnegative().optional().nullable(),
	/** Null = until end of video. */
	overlayTextDurationMs: z.number().int().positive().optional().nullable(),
	speechSpeed: z.number().min(0.75).max(1.35).optional().default(1),
	avatarFileAppId: z.string().min(1).optional().nullable(),
	avatarPhotos: z.array(NarratorAvatarPhotoSchema).optional().nullable(),
	avatarMinDurationMs: z.number().int().positive().optional().nullable(),
	avatarMaxDurationMs: z.number().int().positive().optional().nullable(),
	avatarAppearEveryMs: z.number().int().positive().optional().nullable(),
	avatarIntroHoldMs: z.number().int().nonnegative().optional().nullable(),
	visualDefaults: NarratorVisualDefaultsSchema.optional().nullable(),
	isDefault: z.boolean().optional().default(false),
	isActive: z.boolean().optional().default(true),
});
export type NarratorProfile = z.infer<typeof NarratorProfileSchema>;

export function selectNarratorAvatarFileForAspect(
	profile: {
		avatarPhotos?: unknown;
		avatarFileAppId?: string | null;
	},
	aspect?: string | null,
): string | undefined {
	const rawPhotos = Array.isArray(profile.avatarPhotos)
		? (profile.avatarPhotos as Array<{ fileAppId?: string; aspectRatio?: string }>)
		: [];
	if (aspect) {
		const matching = rawPhotos.find(
			(p) =>
				p.fileAppId &&
				typeof p.fileAppId === "string" &&
				p.aspectRatio === aspect,
		);
		if (matching?.fileAppId?.trim()) return matching.fileAppId.trim();
	}
	if (rawPhotos[0]?.fileAppId?.trim()) return rawPhotos[0].fileAppId.trim();
	return profile.avatarFileAppId?.trim() || undefined;
}

export function visualDefaultsFromProfile(profile: {
	visualDefaults?: unknown;
}): NarratorVisualDefaults | undefined {
	return parseNarratorVisualDefaults(profile.visualDefaults);
}

export function narratorConfigFromProfile(
	profile: {
		id: string;
		narratorName: string;
		gender: "male" | "female";
		provider: "gemini" | "openai";
		voiceId: string;
		personality: string;
		styleNotes?: string | null;
		ttsStyle?: string | null;
		ttsPace?: string | null;
		ttsAccent?: string | null;
		ttsNotes?: string | null;
		channelName?: string | null;
		channelSignOff?: string | null;
		overlayText?: string | null;
		overlayTextDelayMs?: number | null;
		overlayTextDurationMs?: number | null;
		speechSpeed?: number | null;
		avatarFileAppId?: string | null;
		avatarPhotos?: unknown;
	},
	aspect?: string | null,
): NarratorConfig {
	const avatarFileAppId = selectNarratorAvatarFileForAspect(profile, aspect);
	const overlayText = profile.overlayText?.trim() || undefined;
	const delayRaw = profile.overlayTextDelayMs;
	const overlayTextDelayMs =
		typeof delayRaw === "number" &&
		Number.isFinite(delayRaw) &&
		delayRaw >= 0
			? Math.min(
					NARRATOR_OVERLAY_TEXT_DELAY_MAX_MS,
					Math.round(delayRaw),
				)
			: overlayText
				? NARRATOR_OVERLAY_TEXT_DELAY_MS
				: undefined;
	const durationRaw = profile.overlayTextDurationMs;
	const overlayTextDurationMs =
		durationRaw === null
			? null
			: typeof durationRaw === "number" &&
					Number.isFinite(durationRaw) &&
					durationRaw > 0
				? Math.min(
						NARRATOR_OVERLAY_TEXT_DURATION_MAX_MS,
						Math.round(durationRaw),
					)
				: overlayText
					? null
					: undefined;
	return {
		name: profile.narratorName,
		gender: profile.gender,
		provider: profile.provider,
		voiceId: profile.voiceId,
		personality: profile.personality,
		styleNotes: profile.styleNotes?.trim() || undefined,
		ttsStyle: profile.ttsStyle?.trim() || undefined,
		ttsPace: profile.ttsPace?.trim() || undefined,
		ttsAccent: profile.ttsAccent?.trim() || undefined,
		ttsNotes: profile.ttsNotes?.trim() || undefined,
		channelName: profile.channelName?.trim() || undefined,
		channelSignOff: profile.channelSignOff?.trim() || undefined,
		...(overlayText
			? {
					overlayText,
					overlayTextDelayMs:
						overlayTextDelayMs ?? NARRATOR_OVERLAY_TEXT_DELAY_MS,
					overlayTextDurationMs:
						overlayTextDurationMs === undefined
							? null
							: overlayTextDurationMs,
				}
			: {}),
		speechSpeed: normalizeNarratorSpeechSpeed(profile.speechSpeed),
		profileId: profile.id,
		...(avatarFileAppId ? { avatarFileAppId } : {}),
	};
}

export type NarratorOverlayTextFields = Pick<
	NarratorConfig,
	"overlayText" | "overlayTextDelayMs" | "overlayTextDurationMs"
>;

/** Default on-canvas box when brand template has no narratorTextLayout. */
export const DEFAULT_NARRATOR_OVERLAY_TEXT_LAYOUT = {
	left: "5%",
	right: null as string | null,
	top: "78%",
	width: "60%",
};

/**
 * Prefer project document overlay fields; fall back through brief / profile
 * so profile-only edits still compile without a manual Overview save.
 */
export function pickNarratorOverlayTextFields(
	...sources: Array<Partial<NarratorOverlayTextFields> | null | undefined>
): NarratorOverlayTextFields | undefined {
	let overlayText: string | undefined;
	let overlayTextDelayMs: number | undefined;
	let overlayTextDurationMs: number | null | undefined;
	let hasDuration = false;

	for (const source of sources) {
		if (!source) continue;
		const text =
			typeof source.overlayText === "string"
				? source.overlayText.trim()
				: "";
		if (!overlayText && text) overlayText = text;
		if (
			overlayTextDelayMs === undefined &&
			typeof source.overlayTextDelayMs === "number" &&
			Number.isFinite(source.overlayTextDelayMs)
		)
			overlayTextDelayMs = source.overlayTextDelayMs;
		if (!hasDuration && source.overlayTextDurationMs !== undefined) {
			hasDuration = true;
			overlayTextDurationMs = source.overlayTextDurationMs;
		}
	}

	if (!overlayText) return undefined;
	return {
		overlayText,
		overlayTextDelayMs:
			overlayTextDelayMs ?? NARRATOR_OVERLAY_TEXT_DELAY_MS,
		overlayTextDurationMs: hasDuration ? overlayTextDurationMs! : null,
	};
}

/** Resolve compile window for narrator overlay text, or null when empty. */
export function resolveNarratorOverlayTextWindow(input: {
	narrator?: NarratorOverlayTextFields | null;
	videoDurationMs: number;
}): { text: string; fromMs: number; toMs: number } | null {
	const text = input.narrator?.overlayText?.trim() ?? "";
	if (!text) return null;
	const videoDurationMs = Math.max(1, Math.round(input.videoDurationMs || 0));
	const delayMs = Math.min(
		NARRATOR_OVERLAY_TEXT_DELAY_MAX_MS,
		Math.max(
			0,
			Math.round(
				typeof input.narrator?.overlayTextDelayMs === "number"
					? input.narrator.overlayTextDelayMs
					: NARRATOR_OVERLAY_TEXT_DELAY_MS,
			),
		),
	);
	const fromMs = Math.min(delayMs, Math.max(0, videoDurationMs - 1));
	const durationMs = input.narrator?.overlayTextDurationMs;
	const toMs =
		durationMs == null || !(durationMs > 0)
			? videoDurationMs
			: Math.min(
					videoDurationMs,
					fromMs +
						Math.min(
							NARRATOR_OVERLAY_TEXT_DURATION_MAX_MS,
							Math.round(durationMs),
						),
				);
	if (toMs <= fromMs) return null;
	return { text, fromMs, toMs };
}

export const CharacterVoiceSchema = z.object({
	provider: TtsProviderSchema,
	voiceId: z.string().min(1),
});

export const VideoCharacterSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	role: z.string().min(1),
	gender: VoiceGenderSchema.optional(),
	/** Required for consistent performance / dialogue writing */
	personality: z.string().min(1),
	defaultTone: z.string().min(1),
	lookPrompt: VisualPromptSchema,
	/** Required if this character ever speaks (enforced by gates later) */
	voice: CharacterVoiceSchema.optional(),
	/** Script generation — character voice in dialogue writing. */
	speechNotes: z.string().optional(),
	voiceNotes: z.string().optional(),
	/** TTS Director's Notes (optional; falls back to narrator profile defaults). */
	ttsStyle: z.string().min(1).optional(),
	ttsPace: z.string().min(1).optional(),
	ttsAccent: z.string().min(1).optional(),
	ttsNotes: z.string().optional(),
	/** Filled after manual NPC image generation */
	referenceImageUrl: z.string().url().optional().or(z.literal("")),
});
export type VideoCharacter = z.infer<typeof VideoCharacterSchema>;

export const VideoSettingSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	lookPrompt: VisualPromptSchema,
	imageUrl: z.string().url().optional().or(z.literal("")),
});
export type VideoSetting = z.infer<typeof VideoSettingSchema>;

export const VideoPropSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	lookPrompt: VisualPromptSchema,
	imageUrl: z.string().url().optional().or(z.literal("")),
	/**
	 * Continuity role. `product` = locked product photo for advertisement
	 * (do not overwrite imageUrl with AI unless the user regenerates).
	 */
	role: VideoPropRoleSchema.optional().default("prop"),
	/** True when imageUrl came from a user-uploaded / pasted product photo. */
	isProductPhoto: z.boolean().optional(),
});
export type VideoProp = z.infer<typeof VideoPropSchema>;

export const VideoCaptionWordSchema = z.object({
	word: z.string().min(1),
	startMs: z.number().nonnegative(),
	endMs: z.number().positive(),
});
export type VideoCaptionWord = z.infer<typeof VideoCaptionWordSchema>;

export const VideoCaptionSegmentSchema = z.object({
	id: z.string().min(1),
	startMs: z.number().nonnegative(),
	endMs: z.number().positive(),
	text: z.string().min(1),
	words: z.array(VideoCaptionWordSchema).optional(),
});
export type VideoCaptionSegment = z.infer<typeof VideoCaptionSegmentSchema>;

export const BeatDialogueLineSchema = z
	.object({
		id: z.string().min(1),
		speaker: DialogueSpeakerSchema,
		characterId: nullishToOptional(z.string().optional()),
		text: z.string().min(1),
		emotion: nullishToOptional(z.string().optional()),
		tone: nullishToOptional(z.string().optional()),
		audioUrl: nullishToOptional(
			z.string().url().optional().or(z.literal("")),
		),
		/** Pre-TTS estimate (A2+); not authoritative */
		estimatedDurationMs: nullishToOptional(
			z.number().nonnegative().optional(),
		),
		/** Post-TTS measured length (Step 11); authoritative for timing */
		measuredDurationMs: nullishToOptional(
			z.number().nonnegative().optional(),
		),
		/** Raw WAV length at 1.0×, before narrator speechSpeed. */
		sourceDurationMs: nullishToOptional(
			z.number().nonnegative().optional(),
		),
		/** Deepgram clocks for this clip only (0 = start of this WAV). */
		captionSegments: nullishToOptional(
			z.array(VideoCaptionSegmentSchema).optional(),
		),
	})
	.superRefine((line, ctx) => {
		if (line.speaker === "character" && !line.characterId) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "characterId is required when speaker is character",
				path: ["characterId"],
			});
		}
	});
export type BeatDialogueLine = z.infer<typeof BeatDialogueLineSchema>;

/** How dialogue audio is synthesized and compiled. */
export const VideoProjectAudioModeSchema = z.enum(["monologue", "multi_voice"]);
export type VideoProjectAudioMode = z.infer<typeof VideoProjectAudioModeSchema>;

/** One merged voiceover clip per beat (monologue mode). */
export const BeatVoiceoverSchema = z.object({
	audioUrl: nullishToOptional(
		z.string().url().optional().or(z.literal("")),
	),
	/** Post-TTS measured length — authoritative for monologue beat timing */
	measuredDurationMs: nullishToOptional(
		z.number().nonnegative().optional(),
	),
	/** Raw WAV length at 1.0×, before narrator speechSpeed. */
	sourceDurationMs: nullishToOptional(
		z.number().nonnegative().optional(),
	),
});
export type BeatVoiceover = z.infer<typeof BeatVoiceoverSchema>;

export const BeatVisualReferenceIdsSchema = z.object({
	characterIds: z.array(z.string()).optional(),
	settingIds: z.array(z.string()).optional(),
	propIds: z.array(z.string()).optional(),
	includePoster: z.boolean().optional(),
});
export type BeatVisualReferenceIds = z.infer<typeof BeatVisualReferenceIdsSchema>;

/**
 * One keyframe asset on a cinematic plate (start or end).
 * Used by story + advertisement; news/documentary keep flat imageUrl + prompt.
 */
export const PlateFrameAssetSchema = z.object({
	prompt: VisualPromptSchema.optional(),
	url: z.string().url().optional().or(z.literal("")),
	source: PlateFrameSourceSchema.optional(),
	referenceIds: BeatVisualReferenceIdsSchema.optional(),
	/** Other frames to attach as continuity refs during still gen. */
	frameReferences: z.array(PlateFrameReferenceRoleSchema).optional(),
});
export type PlateFrameAsset = z.infer<typeof PlateFrameAssetSchema>;

export const BeatTimingSourceSchema = z.enum([
	"authored",
	"estimated",
	"audio",
]);
export type BeatTimingSource = z.infer<typeof BeatTimingSourceSchema>;

/**
 * One visual segment inside a beat (still or AI video).
 * A beat may have several plates while sharing one dialogue timeline.
 */
export const VideoPlateSchema = z
	.object({
		id: z.string().min(1),
		kind: VisualModalitySchema,
		/** Optional share of the beat; when omitted, UI/compile may split evenly */
		durationMs: z.number().positive().optional(),
		prompt: VisualPromptSchema.optional(),
		/**
		 * 3–8 word named-entity search for real photos (person, place, event).
		 * Used instead of deriving a query from the cinematic prompt.
		 */
		stockQuery: z.preprocess((value) => {
			if (typeof value !== "string") return undefined;
			const trimmed = value.trim();
			return trimmed ? trimmed.slice(0, 120) : undefined;
		}, z.string().max(120).optional()),
		referenceIds: BeatVisualReferenceIdsSchema.optional(),
		imageUrl: z.string().url().optional().or(z.literal("")),
		videoUrl: z.string().url().optional().or(z.literal("")),
		/** Preferred first frame for Veo / Wan (often same as imageUrl after still gen) */
		firstFrameUrl: z.string().url().optional().or(z.literal("")),
		/** Optional end frame for transitions / chaining */
		lastFrameUrl: z.string().url().optional().or(z.literal("")),
		/**
		 * Cinematic start keyframe (story + advertisement).
		 * Prefer over firstFrameUrl/imageUrl; pipeline syncs both.
		 */
		startFrame: PlateFrameAssetSchema.optional(),
		/**
		 * Cinematic end keyframe (story + advertisement).
		 * Prefer over lastFrameUrl; pipeline syncs both.
		 */
		endFrame: PlateFrameAssetSchema.optional(),
		/**
		 * Motion + SFX prompt for AI video (story + advertisement).
		 * Separate from start/end still prompts. No VO / BGM.
		 */
		videoPrompt: VisualPromptSchema.optional(),
		/**
		 * How this plate enters from the previous plate.
		 * Defaults to compile when omitted (legacy projects).
		 */
		enterTransition: PlateEnterTransitionSchema.optional(),
		/**
		 * Generative morph length in ms (Wan `transition_duration`).
		 * Only used when enterTransition=generative. Must be < plate duration.
		 */
		transitionDurationMs: z.number().positive().optional(),
		/**
		 * @deprecated Prefer startFrame.source + enterTransition for story/ads.
		 * Kept for news/doc and older documents.
		 */
		frameContinuity: PlateFrameContinuitySchema.optional(),
		/**
		 * @deprecated Prefer startFrame/endFrame.frameReferences for story/ads.
		 */
		includePreviousPlateAsReference: z.boolean().optional(),
		/** Async AI clip job. Omitted for stock / sync Veo until written. */
		videoStatus: PlateVideoStatusSchema.optional(),
		videoJobId: z.string().optional(),
		videoError: z.string().optional(),
		/** Set after generate — stock (Pexels/Wikimedia) or AI. */
		mediaSource: PlateMediaSourceSchema.optional(),
		stockProvider: StockProviderSchema.optional(),
		stockId: z.string().optional(),
		attribution: z.string().optional(),
		sourcePageUrl: z.string().url().optional().or(z.literal("")),
		stockFileUrl: z.string().url().optional().or(z.literal("")),
	})
	.superRefine((plate, ctx) => {
		if (plate.kind !== "still" && plate.kind !== "ai_video") return;
		const aliased =
			plate.startFrame?.source === "alias_previous_end";
		const hasPrompt =
			Boolean(plate.prompt?.prompt?.trim()) ||
			Boolean(plate.startFrame?.prompt?.prompt?.trim()) ||
			Boolean(plate.videoPrompt?.prompt?.trim()) ||
			Boolean(plate.endFrame?.prompt?.prompt?.trim());
		// Aliased start may omit startFrame.prompt — video/end prompts are enough.
		if (!hasPrompt && !aliased) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					"plate.prompt, startFrame.prompt, or videoPrompt is required for still and ai_video plates",
				path: ["prompt"],
			});
		}
		if (aliased && !hasPrompt) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					"videoPrompt or endFrame.prompt is required when startFrame aliases the previous end",
				path: ["videoPrompt"],
			});
		}
	});
export type VideoPlate = z.infer<typeof VideoPlateSchema>;

/**
 * Beat visuals: prefer `plates[]`.
 * Legacy single-plate fields (`kind`, `prompt`, `imageUrl`, …) remain for back-compat;
 * when `plates` is empty/missing, treat those fields as one implicit plate.
 */
export const BeatVisualSchema = z
	.object({
		/** Legacy / mirror of primary plate kind */
		kind: VisualModalitySchema.optional(),
		prompt: VisualPromptSchema.optional(),
		referenceIds: BeatVisualReferenceIdsSchema.optional(),
		imageUrl: z.string().url().optional().or(z.literal("")),
		videoUrl: z.string().url().optional().or(z.literal("")),
		plates: z.array(VideoPlateSchema).optional(),
	})
	.superRefine((visual, ctx) => {
		const plates = visual.plates;
		if (plates && plates.length > 0) return;

		const kind = visual.kind ?? "still";
		if (
			(kind === "still" || kind === "ai_video") &&
			!visual.prompt?.prompt
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					"visual.prompt is required for still and ai_video beats (or provide plates[])",
				path: ["prompt"],
			});
		}
	})
	.transform((visual) => {
		/** Ensure legacy consumers always see a kind */
		if (visual.kind) return visual;
		const first = visual.plates?.[0];
		return {
			...visual,
			kind: first?.kind ?? ("still" as const),
		};
	});
export type BeatVisual = z.infer<typeof BeatVisualSchema>;

export const VideoBeatSchema = z.object({
	id: z.string().min(1),
	kind: VideoBeatKindSchema,
	durationMs: z.number().positive(),
	/**
	 * How durationMs was chosen.
	 * authored = LLM/UI; estimated = speech formula; audio = measured TTS (authoritative).
	 */
	timingSource: nullishToOptional(BeatTimingSourceSchema.optional()),
	settingId: nullishToOptional(z.string().optional()),
	characterIds: nullishToOptional(z.array(z.string()).optional()),
	visual: BeatVisualSchema,
	onScreenText: nullishToOptional(z.string().optional()),
	textStyle: nullishToOptional(
		z
			.object({
				color: z.string().optional(),
				align: z.enum(["left", "center", "right"]).optional(),
				position: z
					.object({
						top: z.string().optional(),
						left: z.string().optional(),
						width: z.number().optional(),
					})
					.optional(),
			})
			.optional(),
	),
	animations: nullishToOptional(
		z
			.object({
				in: z.enum(["fadeIn", "none"]).optional(),
				out: z.enum(["fadeOut", "none"]).optional(),
			})
			.optional(),
	),
	dialogue: nullishToOptional(z.array(BeatDialogueLineSchema).optional()),
	/** Merged narrator voiceover (monologue mode). */
	voiceover: nullishToOptional(BeatVoiceoverSchema.optional()),
	/** Deepgram word timings for compile-time captions (monologue mode). */
	captionSegments: nullishToOptional(
		z.array(VideoCaptionSegmentSchema).optional(),
	),
});
export type VideoBeat = z.infer<typeof VideoBeatSchema>;

export const ContinuityRuleSchema = z.object({
	id: z.string().min(1),
	rule: z.string().min(1),
});

export const NarrativeConstraintSchema = z.object({
	constraint: z.string().min(1),
	resolution: z.string().min(1),
});

export const VideoProjectBrandSchema = z.object({
	name: z.string().optional(),
	primaryColor: z.string().optional(),
	secondaryColor: z.string().optional(),
	logoUrl: z.string().url().optional().or(z.literal("")),
	fontFamily: z.string().optional(),
});

/** Underscore / BGM selected before compile (full-timeline audio bed). */
export const VideoBackgroundMusicSchema = z.object({
	id: z.string().min(1),
	title: z.string().min(1),
	url: z.string().url(),
	provider: z.enum(["preset", "jamendo", "wikimedia", "custom"]),
	attribution: z.string().optional(),
	sourcePageUrl: z.string().url().optional().or(z.literal("")),
	/** Track length when known (ms) — used to tile/loop under the timeline. */
	durationMs: z.number().positive().optional(),
	/** Mix volume 0–100 (default ~17 under dialogue). */
	volume: z.number().min(0).max(100).default(17),
	/** When true, compile auto-picked this track (user can override). */
	autoSelected: z.boolean().optional(),
	/** Org AppFile created when the remote track was mirrored to S3. */
	appFileId: z.string().min(1).optional(),
});
export type VideoBackgroundMusic = z.infer<typeof VideoBackgroundMusicSchema>;

export const VideoProjectEditorialClaimSchema = z.object({
	id: z.string().min(1),
	beatId: z.string().optional(),
	text: z.string(),
	kind: z
		.enum(["FACT", "INTERPRETATION", "OPINION", "RHETORICAL"])
		.optional(),
	status: z.string().optional(),
	note: z.string().optional(),
});
export type VideoProjectEditorialClaim = z.infer<
	typeof VideoProjectEditorialClaimSchema
>;

export const FictionalStoryIssueSchema = z.object({
	id: z.string().min(1),
	code: z.string().min(1),
	severity: z.enum(["error", "warning"]),
	message: z.string().min(1),
	path: z.string().optional(),
	beatId: z.string().optional(),
});
export type FictionalStoryIssue = z.infer<typeof FictionalStoryIssueSchema>;

export const VideoProjectEditorialReviewSchema = z
	.object({
		at: z.string().optional(),
		status: z
			.enum(["ok", "partial", "skipped", "failed", "reverted"])
			.optional(),
		/** fact_check (news/doc). fictional_story kept for older stored reviews. */
		kind: z.enum(["fact_check", "fictional_story"]).optional(),
		tavilyUsed: z.boolean().optional(),
		changeLog: z.array(z.string()).optional(),
		claims: z.array(VideoProjectEditorialClaimSchema).optional(),
		/** Legacy fictional director issues (no longer written by pipeline). */
		issues: z.array(FictionalStoryIssueSchema).optional(),
		original: z
			.object({
				logline: z.string().optional(),
				dialogue: z
					.array(
						z.object({
							beatId: z.string(),
							lineIndex: z.number(),
							text: z.string(),
						}),
					)
					.optional(),
			})
			.optional(),
		notes: z.record(z.string(), z.unknown()).optional(),
	})
	.passthrough();
export type VideoProjectEditorialReview = z.infer<
	typeof VideoProjectEditorialReviewSchema
>;

export const VideoProjectDocumentSchema = z.object({
	id: z.string().min(1),
	title: z.string().min(1),
	logline: z.string().min(1),
	format: VideoProjectFormatSchema,
	aspect: VideoAspectSchema,
	fps: z.number().int().positive().default(30),
	/**
	 * Actual timeline length — must equal sum(beats[].durationMs).
	 * Not forced to equal the create-form target.
	 */
	durationMs: z.number().positive(),
	/**
	 * Soft budget from create form / inputBrief (guidance only).
	 * Final length may differ after TTS / plate timing.
	 */
	targetDurationMs: z.number().positive().optional(),
	language: z.string().min(2),
	narrator: NarratorConfigSchema,
	/** Planned PIP appearances of the narrator still (lip-sync comes later). */
	narratorAvatar: VideoProjectNarratorAvatarSchema.optional(),
	brand: VideoProjectBrandSchema.optional(),
	/**
	 * Prefer Pexels/Wikimedia stock before AI generation.
	 * Documentary defaults true; story defaults false unless real-world topic.
	 */
	preferRealFootage: z.boolean().optional().default(false),
	/**
	 * Advertisement creative style from create form.
	 * Ignored for other formats.
	 */
	adStyle: AdvertisementStyleSchema.optional(),
	/** Snapshotted from narrator profile — global AI style for plates (style only). */
	visualDefaults: NarratorVisualDefaultsSchema.optional(),
	/** Optional full-timeline background music (selected on Overview before compile). */
	backgroundMusic: VideoBackgroundMusicSchema.optional().nullable(),
	/**
	 * Grounded web sources from the research step (news / documentary / real footage).
	 * Empty when research was skipped or found nothing.
	 */
	sources: z.array(VideoProjectSourceSchema).optional().default([]),
	/** Compact verified fact sheet for LLMs; omit invented specifics when empty. */
	researchNotes: z.string().optional(),
	/**
	 * Harvested / user-provided still URLs tried before web stock search.
	 * Pipeline injects these; LLMs must omit this field.
	 */
	visualRefs: z.array(VideoProjectVisualRefSchema).optional().default([]),
	characters: z.record(z.string(), VideoCharacterSchema).default({}),
	settings: z.record(z.string(), VideoSettingSchema).default({}),
	props: z.record(z.string(), VideoPropSchema).default({}),
	posterPrompt: VisualPromptSchema.optional(),
	posterImageUrl: z.string().url().optional().or(z.literal("")),
	beats: z.array(VideoBeatSchema).min(1),
	continuityRules: z.array(ContinuityRuleSchema).default([]),
	narrativeConstraints: z.array(NarrativeConstraintSchema).default([]),
	/**
	 * Optional end-screen CTA. LLMs often emit `cta: {}` or `cta: null`; treat those
	 * as absent so downstream TTS/compile are not blocked on an unused field.
	 */
	cta: z.preprocess(
		(value) => {
			if (value == null) return undefined;
			if (typeof value !== "object" || Array.isArray(value)) return undefined;
			const label =
				typeof (value as { label?: unknown }).label === "string"
					? (value as { label: string }).label.trim()
					: "";
			if (!label) return undefined;
			const url = sanitizeVideoProjectCtaUrl(
				(value as { url?: unknown }).url,
			);
			return {
				label,
				...(url ? { url } : {}),
			};
		},
		z
			.object({
				label: z.string().min(1),
				url: z.string().url().optional(),
			})
			.optional(),
	),
	/**
	 * AI-written first/pinned social comment for this video (topic-specific).
	 * Not a generic “drop a comment” prompt.
	 */
	socialMediaFirstComment: z.preprocess(
		(value) => sanitizeSocialMediaFirstComment(value),
		z.string().min(1).max(400).optional(),
	),
	metadata: z.object({
		created_by: z.string(),
		pipeline_version: z.string(),
		seed: z.string().optional(),
		/** Snapshotted at lock — drives TTS batching and compile audio strategy. */
		audioMode: VideoProjectAudioModeSchema.optional(),
		/** Text LLM used for authoring / fix (create-form choice). */
		textProvider: z.enum(["deepseek", "gemini"]).optional(),
		/** Stable id shared by all language variants of the same story. */
		translationGroupId: z.string().min(1).optional(),
		/** When this document was created via language copy. */
		sourceProjectId: z.string().min(1).optional(),
		sourceLanguage: z.string().min(2).optional(),
		translationOfJobId: z.string().min(1).optional(),
		/** Post-polish editorial review snapshot (claims, change log, originals). */
		editorialReview: VideoProjectEditorialReviewSchema.optional(),
	}),
});
export type VideoProjectDocument = z.infer<typeof VideoProjectDocumentSchema>;

/**
 * Create-form / locked inputs stored on VideoProject.inputBrief.
 * `durationMs` is a soft target budget for authoring — not the forced final MP4 length.
 */
export const VideoProjectInputBriefSchema = z
	.object({
		theme: z.string().optional(),
		brief: z.string().optional(),
		seed: z.string().optional(),
		generate: z.enum(["theme", "brief", "both", "elaborate"]).optional(),
		format: VideoProjectFormatSchema,
		aspect: VideoAspectSchema,
		/**
		 * Soft target duration (budget). Alias: targetDurationMs.
		 * Final document.durationMs is sum(beats) and may diverge after TTS.
		 */
		durationMs: z.number().positive().optional(),
		/** Explicit alias for durationMs (soft target) */
		targetDurationMs: z.number().positive().optional(),
		language: z.string().min(2).default("en"),
		narrator: NarratorConfigSchema,
		/** Required at create — org narrator profile used to fill `narrator`. */
		narratorProfileId: z.string().min(1).optional(),
		brandId: z.string().optional(),
		allowCharacters: z.boolean().optional(),
		preferredVisualKind: VisualModalitySchema.optional(),
		/**
		 * When true (e.g. real-world story), run grounded research like news/doc.
		 * News/documentary always research regardless of this flag.
		 * Advertisement never uses this for fact research.
		 */
		preferRealFootage: z.boolean().optional(),
		/** Advertisement: creative style (create form). */
		adStyle: AdvertisementStyleSchema.optional(),
		/** Snapshotted from narrator profile — global AI style for plates (style only). */
		visualDefaults: NarratorVisualDefaultsSchema.optional(),
		/** Optional source article URLs pasted at create (merged into research). */
		sourceUrls: z.array(z.string().url()).max(20).optional(),
		/** Optional image URLs + captions pasted at create / from trending. */
		visualRefs: z.array(VideoProjectVisualRefSchema).max(24).optional(),
		/** Advertisement: optional product display name. */
		productName: z.string().max(120).optional(),
		/** Advertisement: primary product packshot URL (injected as Items product). */
		productPhotoUrl: z.string().url().optional().or(z.literal("")),
		/** Advertisement: AppFile id when product photo was chosen from the library. */
		productPhotoAppFileId: z.string().min(1).optional(),
		/** Advertisement: offer / CTA label hint (e.g. Shop now). */
		productOffer: z.string().max(200).optional(),
		/** Advertisement: landing URL for offer CTA (must be real; not invented by LLM). */
		productLandingUrl: z.string().url().optional().or(z.literal("")),
	})
	.superRefine((brief, ctx) => {
		if (
			brief.durationMs == null &&
			brief.targetDurationMs == null
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "durationMs (or targetDurationMs) is required",
				path: ["durationMs"],
			});
		}
	})
	.transform((brief) => {
		const target = brief.durationMs ?? brief.targetDurationMs;
		return {
			...brief,
			durationMs: target as number,
			targetDurationMs: brief.targetDurationMs ?? target,
		};
	});
export type VideoProjectInputBrief = z.infer<typeof VideoProjectInputBriefSchema>;

/**
 * Create API body brief — narrator is filled server-side from `narratorProfileId`.
 */
export const VideoProjectCreateInputBriefSchema = z
	.object({
		theme: z.string().optional(),
		brief: z.string().optional(),
		seed: z.string().optional(),
		generate: z.enum(["theme", "brief", "both", "elaborate"]).optional(),
		format: VideoProjectFormatSchema,
		aspect: VideoAspectSchema,
		durationMs: z.number().positive().optional(),
		targetDurationMs: z.number().positive().optional(),
		language: z.string().min(2).default("en"),
		brandId: z.string().optional(),
		allowCharacters: z.boolean().optional(),
		preferredVisualKind: VisualModalitySchema.optional(),
		preferRealFootage: z.boolean().optional(),
		adStyle: AdvertisementStyleSchema.optional(),
		sourceUrls: z.array(z.string().url()).max(20).optional(),
		visualRefs: z.array(VideoProjectVisualRefSchema).max(24).optional(),
		productName: z.string().max(120).optional(),
		productPhotoUrl: z.string().url().optional().or(z.literal("")),
		productPhotoAppFileId: z.string().min(1).optional(),
		productOffer: z.string().max(200).optional(),
		productLandingUrl: z.string().url().optional().or(z.literal("")),
	})
	.superRefine((brief, ctx) => {
		if (brief.durationMs == null && brief.targetDurationMs == null) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "durationMs (or targetDurationMs) is required",
				path: ["durationMs"],
			});
		}
	})
	.transform((brief) => {
		const target = brief.durationMs ?? brief.targetDurationMs;
		return {
			...brief,
			durationMs: target as number,
			targetDurationMs: brief.targetDurationMs ?? target,
		};
	});
export type VideoProjectCreateInputBrief = z.infer<
	typeof VideoProjectCreateInputBriefSchema
>;

export const VideoValidationErrorSchema = z.object({
	code: z.string(),
	message: z.string(),
	path: z.string().optional(),
});
export type VideoValidationError = z.infer<typeof VideoValidationErrorSchema>;

export const VideoAutoFixSuggestionSchema = z.object({
	path: z.string(),
	action: z.enum(["set", "add", "remove", "replace"]),
	value: z.unknown().optional(),
	reason: z.string(),
});
export type VideoAutoFixSuggestion = z.infer<typeof VideoAutoFixSuggestionSchema>;

export const VideoValidationReportSchema = z.object({
	isValid: z.boolean(),
	/** Exact LLM success string when valid: "project is valid" */
	rawVerdict: z.string().optional(),
	severity: z.enum(["error", "warning"]).optional(),
	errors: z.array(VideoValidationErrorSchema).default([]),
	warnings: z.array(VideoValidationErrorSchema).default([]),
	autoFixSuggestions: z.array(VideoAutoFixSuggestionSchema).default([]),
	fixIterations: z.number().int().nonnegative().default(0),
});
export type VideoValidationReport = z.infer<typeof VideoValidationReportSchema>;

/** True when generation failed during validate→fix (initial or post-polish). */
export function isVideoProjectFixValidationFailure(
	message: string | undefined,
): boolean {
	if (!message?.trim()) return false;
	const normalized = message.trim();
	if (
		normalized.includes("failed validation after") &&
		(normalized.includes("fix iteration") ||
			normalized.includes("after polish"))
	)
		return true;
	if (/failed after \d+ fix iteration/i.test(normalized)) return true;
	if (/fix iteration\(s\)/i.test(normalized) && /validat/i.test(normalized))
		return true;
	return false;
}

function asVideoProjectRecord(value: unknown): Record<string, unknown> | null {
	return value && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null;
}

/** Source project id for a language copy (including failed translate shells). */
export function getVideoProjectTranslationSourceId(project: {
	document?: unknown;
	inputBrief?: unknown;
}): string | null {
	const doc = asVideoProjectRecord(project.document);
	const brief = asVideoProjectRecord(project.inputBrief);
	const meta = asVideoProjectRecord(doc?.metadata);
	const fromMeta =
		typeof meta?.sourceProjectId === "string"
			? meta.sourceProjectId.trim()
			: "";
	if (fromMeta) return fromMeta;
	const fromBrief =
		typeof brief?.translationSourceProjectId === "string"
			? brief.translationSourceProjectId.trim()
			: "";
	return fromBrief || null;
}

/** Whether this project can re-localize from its original source script. */
export function canRetryVideoProjectTranslation(project: {
	status: string;
	document?: unknown;
	inputBrief?: unknown;
}): boolean {
	if (project.status === "PROCESSING") return false;
	return Boolean(getVideoProjectTranslationSourceId(project));
}

/** Whether a failed project can re-run validate→fix on its saved draft. */
export function canRetryVideoProjectFixes(project: {
	status: string;
	document?: unknown;
	validation?: unknown;
	errorMessage?: string[] | string | null;
}): boolean {
	if (project.status !== "FAILED") return false;

	const doc =
		project.document &&
		typeof project.document === "object" &&
		!Array.isArray(project.document)
			? (project.document as Record<string, unknown>)
			: null;
	const beats = Array.isArray(doc?.beats) ? doc.beats : [];
	if (beats.length === 0) return false;

	const messages = Array.isArray(project.errorMessage)
		? project.errorMessage
		: typeof project.errorMessage === "string" && project.errorMessage.trim()
			? [project.errorMessage]
			: [];
	const headline = messages[0] ?? messages.join(" ");
	if (isVideoProjectFixValidationFailure(headline)) return true;

	const validation =
		project.validation &&
		typeof project.validation === "object" &&
		!Array.isArray(project.validation)
			? (project.validation as Record<string, unknown>)
			: null;
	const errors = Array.isArray(validation?.errors) ? validation.errors : [];
	if (errors.length > 0) return true;
	if (validation?.isValid === false) return true;

	return false;
}

/** Exact string the validation LLM must return when the project passes */
export const VIDEO_PROJECT_VALID_VERDICT = "project is valid" as const;

export interface PublishedStoryRow {
	id: string;
	title: string;
	document?: Record<string, unknown> | null;
	publishTag?: unknown;
	episodeSummary?: string | null;
	inputBrief?: Record<string, unknown>;
}

export function nextStoryEpisodeNumber(rows: PublishedStoryRow[]): number {
	let max = 0;
	for (const row of rows) {
		const tag = parseStoryPublishTag(row.publishTag);
		if (tag?.type === "story" && tag.episodeNumber > max)
			max = tag.episodeNumber;
	}
	return max + 1;
}

export function buildEpisodeSummary(input: {
	title: string;
	logline?: string;
}): string {
	const title = input.title.trim();
	const logline = input.logline?.trim() ?? "";
	if (logline && logline !== title) return `${title}. ${logline}`.slice(0, 400);
	return title.slice(0, 400);
}
